/**
 * ONE PICTURE, FROM A FILE ON THE AUTHOR'S DISK TO A MEDIA ID THE DRAFT CAN NAME.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * ★★ TWO LEGS, AND ONLY THE SECOND ONE IS A CALL TO US
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * The browser writes the bytes STRAIGHT INTO its own Firebase Storage quarantine prefix,
 * `uploads/incoming/{uid}/{uuid}` (adr/0010-firebase-integration.md:13), through the one
 * `QuarantineUploader` the app has (features/storage/create-quarantine-uploader.ts - the fixture in
 * the default build, `firebase/storage` in a live one). Only then does the editor call
 * `claimArticleImageUpload` naming that object, and THE CLAIM IS THE FINALIZE STEP: the platform
 * validates, re-encodes and answers our media id and the public rendition's address
 * (authors-api.yaml:1285-1296). This hook follows the shape of the application form's two uploads,
 * which share the mechanism: the browser writes, "and only then calls the claim operation to name
 * that object" (features/application/data/useUpload.ts:4-9).
 *
 * ★ THE INTAKE ID IS MINTED ONCE PER FILE, AND IT IS A UUID. It is the idempotency key, so a retry
 * of the same object reuses it - and the claim refuses anything that is not UUID-shaped with
 * `UPLOAD_OBJECT_NOT_FOUND` before it reads the bucket (`UUID_SHAPE`,
 * article-image-claim.service.ts:146, applied at :202-207). `crypto.randomUUID()` is a v4, which
 * that shape admits; `newBlockId`'s v7 is for BLOCKS and needs a server instant (./document.ts).
 *
 * ★ ONE UPLOAD PER CONTROL, AND A LATE ANSWER NEVER LANDS. Choosing a second file while the first
 * is in flight aborts the first; so does leaving the screen, and so does the article turning
 * read-only (./ImageUploadButton.tsx). The abort tells the writer to stop, and every step after an
 * `await` checks that its upload is still the current one - a writer that finished anyway, or a
 * claim that answered after the author moved on, changes nothing on screen and in the draft.
 *
 * ★ `claimArticleImageUpload` TAKES NO SIGNAL (features/cabinet/api/editor-api.ts:181), so a
 * claim already sent cannot be recalled. That costs an unreferenced stored file, never a wrong
 * draft: the answer is dropped here.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useEditorApi } from '../../../features/cabinet/api/editor-api';
import { ERROR_CODE, describeProblem, hasErrorCode } from '../../../features/cabinet/api/problem';
import { ApiError, TransportError, UploadStalled } from '../../../features/application/api/problem';
import type { QuarantineUploader } from '../../../features/application/api/types';
import type { ArticleImageClaim, StoredImage } from '../../../features/cabinet/api/editor-api';
import type { ErrorCode } from '../../../features/cabinet/api/generated';
import type { ArticlesT } from '../articles-i18n';
import { useUploadsInFlight } from './uploads-in-flight';

/** What the picture is for. A cover's size is checked at submit, a body picture's at claim. */
export type ArticleImageRole = ArticleImageClaim['role'];

/**
 * ★ THE CEILING, AND IT IS `<`, NOT `<=`. The quarantine rule admits a write only while
 * "request.resource.size < 20 * 1024 * 1024" (adr/0010-firebase-integration.md:21), and the
 * claim's own ceiling is `max_upload_bytes` 20971520 on both article kinds
 * (04-schema.md:10879-10880). Storage refuses an oversized write with a bare 403 that says nothing
 * an author can act on, so the size is checked HERE, before a single byte moves, where the refusal
 * can say why.
 */
export const MAX_IMAGE_BYTES = 20 * 1024 * 1024;

/** `ArticleImageClaim.originalName`: at most 200 (authors-api.yaml:4156-4158). */
const ORIGINAL_NAME_MAX = 200;

export type ArticleImageUploadState =
  | { readonly phase: 'idle' }
  /** Writing the bytes into the quarantine prefix. `progress` is 0…1. */
  | { readonly phase: 'uploading'; readonly progress: number }
  /** The claim is out: the platform is validating and re-encoding. It reports no progress. */
  | { readonly phase: 'claiming' }
  | { readonly phase: 'done'; readonly image: StoredImage }
  | { readonly phase: 'failed'; readonly error: unknown };

const IDLE: ArticleImageUploadState = { phase: 'idle' };

/** The one refusal made in this file, before the writer is asked for anything. */
export class ImageTooLarge extends Error {
  override readonly name = 'ImageTooLarge';
}

export interface ArticleImageUploadOptions {
  readonly articleId: string;
  readonly role: ArticleImageRole;
  /**
   * Called once, with the stored image, when the claim has answered. Read when the answer arrives
   * rather than when the file was chosen, so it lands on the draft that is on screen then.
   */
  readonly onClaimed: (image: StoredImage) => void;
}

export interface ArticleImageUpload {
  readonly state: ArticleImageUploadState;
  /**
   * Upload `file` and claim it. Aborts any upload this control already has in flight. Resolves
   * with the stored image, or `undefined` when it was refused, replaced or abandoned.
   */
  readonly upload: (file: File) => Promise<StoredImage | undefined>;
  /** Abandon the upload in flight, if there is one, and go back to idle. */
  readonly cancel: () => void;
}

/* ---------------------------------------------------------------------------------------------
 * The writer, from the screen that owns it.
 * ------------------------------------------------------------------------------------------- */

const EditorUploaderContext = createContext<QuarantineUploader | null>(null);

/**
 * The editor's writer for its own subtree, provided beside `EditorApiProvider` by
 * ../EditorRoute.tsx - which builds it with `createQuarantineUploader()` unless one is handed in.
 */
export const EditorUploaderProvider = EditorUploaderContext.Provider;

export function useEditorUploader(): QuarantineUploader {
  const uploader = useContext(EditorUploaderContext);
  if (uploader === null) {
    throw new Error(
      'No QuarantineUploader in context. The editor screen provides one for its own subtree - ' +
        'see routes/articles/EditorRoute.tsx, which builds it through ' +
        'features/storage/create-quarantine-uploader.ts.',
    );
  }
  return uploader;
}

/* ---------------------------------------------------------------------------------------------
 * The upload.
 * ------------------------------------------------------------------------------------------- */

/** 0…1, as the seam promises (features/application/api/types.ts); anything else is clamped. */
function fractionOf(value: number): number {
  return Number.isFinite(value) ? Math.min(Math.max(value, 0), 1) : 0;
}

/** The name the file arrived with. An empty one is a 400 (`minLength: 1`), so null is sent. */
function originalNameOf(file: File): string | null {
  const name = file.name.slice(0, ORIGINAL_NAME_MAX);
  return name.length === 0 ? null : name;
}

export function useArticleImageUpload({
  articleId,
  role,
  onClaimed,
}: ArticleImageUploadOptions): ArticleImageUpload {
  const api = useEditorApi();
  const uploader = useEditorUploader();
  const uploads = useUploadsInFlight();
  const [state, setState] = useState<ArticleImageUploadState>(IDLE);

  /** The upload in flight, or null. Identity is what "still current" means below. */
  const inFlight = useRef<AbortController | null>(null);
  const onClaimedRef = useRef(onClaimed);
  onClaimedRef.current = onClaimed;

  useEffect(
    () => () => {
      // Leaving the screen abandons the upload: nothing is left to show it on.
      if (inFlight.current !== null) {
        inFlight.current.abort();
        inFlight.current = null;
        // An `<Activity>` hide keeps this state, and a phase left at "uploading" would hold Send
        // for good once shown again. On a real unmount React drops the update.
        setState(IDLE);
      }
    },
    [],
  );

  /* ★ COUNTED IN WHILE IT IS GOING IN, so the screen holds Send until the picture is in - claimed,
     and on the draft in the same render - or has failed (./uploads-in-flight.ts). Taken in the
     effect's body and dropped by its cleanup: StrictMode's teardown cannot lose it or double it. */
  const goingIn = state.phase === 'uploading' || state.phase === 'claiming';
  useEffect(() => (goingIn ? uploads.hold() : undefined), [goingIn, uploads]);

  const upload = useCallback(
    async (file: File): Promise<StoredImage | undefined> => {
      // A NEW FILE REPLACES THE ONE IN FLIGHT. The author chose again; the first answer is moot.
      inFlight.current?.abort();
      inFlight.current = null;
      if (file.size >= MAX_IMAGE_BYTES) {
        setState({
          phase: 'failed',
          error: new ImageTooLarge(`The file is ${String(file.size)} bytes.`),
        });
        return undefined;
      }

      const controller = new AbortController();
      inFlight.current = controller;
      const current = (): boolean => inFlight.current === controller;
      const intakeId = crypto.randomUUID();

      setState({ phase: 'uploading', progress: 0 });
      try {
        await uploader.putQuarantineObject(
          intakeId,
          file,
          (fraction) => {
            if (current()) {
              setState({ phase: 'uploading', progress: fractionOf(fraction) });
            }
          },
          { signal: controller.signal },
        );
        if (!current()) {
          return undefined;
        }
        setState({ phase: 'claiming' });
        const image = await api.claimArticleImageUpload(articleId, {
          intakeId,
          role,
          originalName: originalNameOf(file),
        });
        if (!current()) {
          return undefined;
        }
        inFlight.current = null;
        setState({ phase: 'done', image });
        onClaimedRef.current(image);
        return image;
      } catch (cause) {
        // An abort lands here too, and is not a failure: whoever aborted has moved the control on.
        if (!current()) {
          return undefined;
        }
        inFlight.current = null;
        setState({ phase: 'failed', error: cause });
        return undefined;
      }
    },
    [api, uploader, articleId, role],
  );

  const cancel = useCallback(() => {
    if (inFlight.current === null) {
      return;
    }
    inFlight.current.abort();
    inFlight.current = null;
    setState(IDLE);
  }, []);

  return { state, upload, cancel };
}

/* ---------------------------------------------------------------------------------------------
 * The sentences.
 * ------------------------------------------------------------------------------------------- */

/**
 * The claim's five refusals: 404 `UPLOAD_OBJECT_NOT_FOUND` for an object that is not there, and
 * 422 `UPLOAD_TYPE_REJECTED`, `UPLOAD_TOO_LARGE`, `UPLOAD_TOO_SMALL` or
 * `UPLOAD_DIMENSIONS_REJECTED` for one that is not a usable image (authors-api.yaml:1332, :1338).
 * Typed `ErrorCode`, so a misspelt code does not compile.
 */
const UPLOAD_REFUSAL = {
  objectNotFound: 'UPLOAD_OBJECT_NOT_FOUND',
  typeRejected: 'UPLOAD_TYPE_REJECTED',
  tooLarge: 'UPLOAD_TOO_LARGE',
  tooSmall: 'UPLOAD_TOO_SMALL',
  dimensionsRejected: 'UPLOAD_DIMENSIONS_REJECTED',
} as const satisfies Readonly<Record<string, ErrorCode>>;

const MAX_IMAGE_MB = String(MAX_IMAGE_BYTES / (1024 * 1024));

/**
 * What an author reads when a picture did not make it in. Branches on the machine code, never on
 * the server's prose, and falls through to the editor's own `describeProblem` for everything that
 * is not about the picture (features/cabinet/api/problem.ts).
 *
 * TWO FAMILIES OF FAILURE ARRIVE HERE. The claim throws the cabinet's `ApiProblem`; the WRITER
 * throws the application feature's `TransportError` and `ApiError`, the shapes both of its
 * implementations share (`toUploadError`, features/storage/firebase-quarantine-uploader.ts). A
 * Storage refusal carries a status and no code - Storage does not answer in RFC 9457.
 */
export function describeImageUploadFailure(error: unknown, t: ArticlesT): string {
  if (error instanceof ImageTooLarge) {
    return t('editor.upload.fileTooLarge', { mb: MAX_IMAGE_MB });
  }
  if (hasErrorCode(error, UPLOAD_REFUSAL.objectNotFound)) {
    return t('editor.upload.notArrived');
  }
  if (hasErrorCode(error, UPLOAD_REFUSAL.typeRejected)) {
    return t('editor.upload.wrongType');
  }
  if (hasErrorCode(error, UPLOAD_REFUSAL.tooLarge)) {
    return t('editor.upload.largerThan', { mb: MAX_IMAGE_MB });
  }
  if (hasErrorCode(error, UPLOAD_REFUSAL.tooSmall)) {
    // THE BODY PICTURE'S FLOOR, the seeded `article_image` row (04-schema.md:10880). A cover is
    // never refused for size at claim - its floor "is checked at submit rather than here"
    // (authors-api.yaml:1294-1295) - so this code only ever answers a body picture. The row is
    // "one `UPDATE` if experience says otherwise" (04-schema.md:10887): change both together.
    return t('editor.upload.tooSmall');
  }
  if (hasErrorCode(error, UPLOAD_REFUSAL.dimensionsRejected)) {
    return t('editor.upload.unreadable');
  }
  if (hasErrorCode(error, ERROR_CODE.notArticleOwner)) {
    // `describeProblem` words this code for the comments inbox, which would be the wrong sentence.
    return t('editor.upload.notYours');
  }
  if (error instanceof UploadStalled) {
    // Before the transport sentence, whose class it extends: the writer stopped a write that hung.
    return t('editor.upload.stalled');
  }
  if (error instanceof TransportError) {
    return t('editor.upload.transport');
  }
  if (error instanceof ApiError) {
    return error.status === 401
      ? t('editor.upload.signedOut')
      : t('editor.upload.storageRefused', { mb: MAX_IMAGE_MB });
  }
  return describeProblem(error);
}
